import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import SubscriptionRequiredModal from '../../../components/ui/SubscriptionRequiredModal';
import { isSubscriptionError } from '../../../lib/subscriptionGate';
import QuizBuilderModal, { type QuizDraft } from './QuizBuilderModal';

type CatalogStructure = 'semestru' | 'trimestru' | 'module';

type GradebookConfigLite = {
  id: number;
  school_year: string;
  structure: CatalogStructure | string;
};

type CatalogSubjectLite = {
  id: number;
  name: string;
  professor_id?: number | null;
  professor_name?: string | null;
  gradebook_config_id?: number | null;
  period_index?: number | null;
};

type HomeworkFile = {
  id: number;
  file_url: string;
  original_name: string;
};

type HomeworkItem = {
  id: number;
  subject_id: number | null;
  subject: string | null;
  title: string;
  description: string | null;
  created_at: string;
  has_quiz?: boolean;
  quiz_time_limit_seconds?: number | null;
  files: HomeworkFile[];
};

type HomeworkSubmissionFile = { id: number; file_url: string; original_name: string };
type HomeworkSubmission = {
  id: number;
  student: { id: number | null; full_name: string | null; email: string | null };
  comment: string | null;
  created_at: string;
  updated_at: string;
  files: HomeworkSubmissionFile[];
};

type QuizSubmission = {
  id: number;
  student: { id: number | null; full_name: string | null; email: string | null };
  started_at: string | null;
  submitted_at: string | null;
  duration_seconds: number | null;
  score: number | null;
  total: number | null;
};

interface Props {
  classroomId: number;
}

function cfgLabel(cfg: GradebookConfigLite) {
  const s = (cfg.structure || '').toLowerCase();
  const struct = s === 'semestru' ? 'Semestru' : s === 'trimestru' ? 'Trimestru' : 'Module';
  return `${cfg.school_year} • ${struct}`;
}

function periodLabels(structure: CatalogStructure | string): string[] {
  const s = (structure || '').toString().toLowerCase();
  if (s === 'semestru') return ['Semestrul 1', 'Semestrul 2'];
  if (s === 'trimestru') return ['Trimestrul 1', 'Trimestrul 2', 'Trimestrul 3'];
  return ['Modul 1', 'Modul 2', 'Modul 3', 'Modul 4', 'Modul 5'];
}

const TemePanel: React.FC<Props> = ({ classroomId }) => {
  const { me } = useAuth();
  const apiBase = useMemo(() => (api.defaults.baseURL || 'http://localhost:8000').toString().replace(/\/$/, ''), []);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [configs, setConfigs] = useState<GradebookConfigLite[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const selectedConfig = useMemo(
    () => configs.find((c) => Number(c.id) === Number(selectedConfigId)) || null,
    [configs, selectedConfigId]
  );
  const [periodIndex, setPeriodIndex] = useState(0);

  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjects, setSubjects] = useState<CatalogSubjectLite[]>([]);
  const mySubjects = useMemo(() => {
    if (!me?.id) return [];
    const mine = subjects.filter((s) => Number(s.professor_id) === Number(me.id));
    return mine.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [me?.id, subjects]);

  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);

  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [quizDraft, setQuizDraft] = useState<QuizDraft | null>(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizEditingTemaId, setQuizEditingTemaId] = useState<number | null>(null);
  const [quizEditDraft, setQuizEditDraft] = useState<QuizDraft | null>(null);

  const [teme, setTeme] = useState<HomeworkItem[]>([]);

  const doDownloadHomeworkFile = async (fileId: number) => {
    try {
      const resp = await api.get(`/api/teme/fisiere/${encodeURIComponent(String(fileId))}/download`);
      const urlPath = String((resp.data as any)?.url || '');
      const base = String((api.defaults.baseURL || window.location.origin) as any).replace(/\/$/, '');
      const fullUrl = urlPath ? `${base}${urlPath.startsWith('/') ? urlPath : `/${urlPath}`}` : '';
      if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        setUpgradeOpen(true);
        return;
      }
      alert(e?.response?.data?.detail || 'Nu am putut descărca fișierul.');
    }
  };

  const doDownloadSubmissionFile = async (fileId: number) => {
    try {
      const resp = await api.get(`/api/teme/rezolvari/fisiere/${encodeURIComponent(String(fileId))}/download`);
      const urlPath = String((resp.data as any)?.url || '');
      const base = String((api.defaults.baseURL || window.location.origin) as any).replace(/\/$/, '');
      const fullUrl = urlPath ? `${base}${urlPath.startsWith('/') ? urlPath : `/${urlPath}`}` : '';
      if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        setUpgradeOpen(true);
        return;
      }
      alert(e?.response?.data?.detail || 'Nu am putut descărca fișierul.');
    }
  };
  const [solutionsOpen, setSolutionsOpen] = useState<Record<number, boolean>>({});
  const [solutionsByTemaId, setSolutionsByTemaId] = useState<Record<number, HomeworkSubmission[] | undefined>>({});
  const [solutionsLoadingByTemaId, setSolutionsLoadingByTemaId] = useState<Record<number, boolean>>({});
  const [selectedStudentByTemaId, setSelectedStudentByTemaId] = useState<Record<number, string>>({});

  const [quizSubsOpen, setQuizSubsOpen] = useState<Record<number, boolean>>({});
  const [quizSubsByTemaId, setQuizSubsByTemaId] = useState<Record<number, QuizSubmission[] | undefined>>({});
  const [quizSubsLoadingByTemaId, setQuizSubsLoadingByTemaId] = useState<Record<number, boolean>>({});
  const [selectedStudentByQuizTemaId, setSelectedStudentByQuizTemaId] = useState<Record<number, string>>({});

  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newComment, setNewComment] = useState('');

  const loadConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/catalog/config/all/${classroomId}`);
      const list = Array.isArray(resp.data) ? (resp.data as GradebookConfigLite[]) : [];
      setConfigs(list);
      if (!selectedConfigId && list.length) setSelectedConfigId(list[0].id);
      if (selectedConfigId && list.every((c) => Number(c.id) !== Number(selectedConfigId))) setSelectedConfigId(list[0]?.id ?? null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu s-a putut incarca lista de cataloage.');
      setConfigs([]);
      setSelectedConfigId(null);
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async (cfgId: number, pIdx: number) => {
    setSubjectsLoading(true);
    try {
      const resp = await api.get(`/api/catalog/classroom/${classroomId}/overview`, { params: { config_id: cfgId, period_index: pIdx } });
      const list = Array.isArray(resp.data?.subjects) ? (resp.data.subjects as CatalogSubjectLite[]) : [];
      setSubjects(list);
    } catch {
      setSubjects([]);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const loadTeme = async (cfgId: number, pIdx: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/teme/lista/${classroomId}`, { params: { gradebook_config_id: cfgId, period_index: pIdx } });
      setTeme(Array.isArray(res.data) ? (res.data as HomeworkItem[]) : []);
    } catch (err: any) {
      setTeme([]);
      setError(err?.response?.data?.detail || 'Eroare la incarcarea temelor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroomId]);

  useEffect(() => {
    if (!selectedConfigId) return;
    setPeriodIndex(0);
  }, [selectedConfigId]);

  useEffect(() => {
    if (!selectedConfigId) {
      setSubjects([]);
      setSelectedSubjectId(null);
      setTeme([]);
      return;
    }
    void loadSubjects(selectedConfigId, periodIndex);
    void loadTeme(selectedConfigId, periodIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroomId, selectedConfigId, periodIndex]);

  useEffect(() => {
    if (!mySubjects.length) {
      setSelectedSubjectId(null);
      return;
    }
    if (selectedSubjectId && mySubjects.some((s) => Number(s.id) === Number(selectedSubjectId))) return;
    setSelectedSubjectId(mySubjects[0].id);
  }, [mySubjects, selectedSubjectId]);

  const grouped = useMemo(() => {
    const map = new Map<string, { subjectId: number | null; subjectName: string; items: HomeworkItem[] }>();
    for (const t of teme) {
      const id = t.subject_id ?? null;
      const name = (t.subject || '').toString().trim() || (id == null ? 'Fara materie' : `Materia #${id}`);
      const key = id == null ? `null:${name}` : String(id);
      const cur = map.get(key);
      if (cur) cur.items.push(t);
      else map.set(key, { subjectId: id, subjectName: name, items: [t] });
    }
    return Array.from(map.values()).sort((a, b) => a.subjectName.localeCompare(b.subjectName));
  }, [teme]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConfigId) {
      setError('Selecteaza un catalog.');
      return;
    }
    if (selectedSubjectId == null) {
      setError('Selecteaza materia.');
      return;
    }
    if (!title.trim()) {
      setError('Completeaza titlul temei.');
      return;
    }

    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const formData = new FormData();
      formData.append('classroom_id', String(classroomId));
      formData.append('subject_id', String(selectedSubjectId));
      formData.append('gradebook_config_id', String(selectedConfigId));
      formData.append('period_index', String(periodIndex));
      formData.append('title', title.trim());
      formData.append('comment', comment);
      files.forEach((f) => formData.append('files', f));
      if (quizDraft) formData.append('quiz_json', JSON.stringify(quizDraft));

      await api.post('/api/teme/adauga', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setTitle('');
      setComment('');
      setFiles([]);
      setQuizDraft(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setInfo('Tema a fost adaugata.');
      await loadTeme(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la adaugarea temei.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTema = async (id: number) => {
    if (!window.confirm('Stergi tema?')) return;
    if (!selectedConfigId) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      await api.delete(`/api/teme/sterge/${id}`);
      setInfo('Tema a fost stearsa.');
      await loadTeme(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la stergerea temei.');
    } finally {
      setLoading(false);
    }
  };

  const loadSolutions = async (temaId: number) => {
    setSolutionsLoadingByTemaId((p) => ({ ...p, [temaId]: true }));
    setError(null);
    try {
      const resp = await api.get(`/api/teme/${temaId}/rezolvari`);
      const list = Array.isArray(resp.data) ? (resp.data as HomeworkSubmission[]) : [];
      setSolutionsByTemaId((p) => ({ ...p, [temaId]: list }));
      setSelectedStudentByTemaId((p) => {
        const cur = p[temaId];
        if (cur && list.some((s) => String(s.student?.id) === String(cur))) return p;
        const first = list[0]?.student?.id != null ? String(list[0].student.id) : '';
        return { ...p, [temaId]: first };
      });
    } catch (err: any) {
      setSolutionsByTemaId((p) => ({ ...p, [temaId]: [] }));
      setError(err?.response?.data?.detail || 'Eroare la incarcarea rezolvarilor.');
    } finally {
      setSolutionsLoadingByTemaId((p) => ({ ...p, [temaId]: false }));
    }
  };

  const loadQuizSubmissions = async (temaId: number) => {
    setQuizSubsLoadingByTemaId((p) => ({ ...p, [temaId]: true }));
    setError(null);
    try {
      const resp = await api.get(`/api/teme/${temaId}/quiz/submissions`);
      const list = Array.isArray(resp.data) ? (resp.data as QuizSubmission[]) : [];
      setQuizSubsByTemaId((p) => ({ ...p, [temaId]: list }));
      setSelectedStudentByQuizTemaId((p) => {
        const cur = p[temaId];
        if (cur && list.some((s) => String(s.student?.id) === String(cur))) return p;
        const first = list[0]?.student?.id != null ? String(list[0].student.id) : '';
        return { ...p, [temaId]: first };
      });
    } catch (err: any) {
      setQuizSubsByTemaId((p) => ({ ...p, [temaId]: [] }));
      setError(err?.response?.data?.detail || 'Eroare la incarcarea rezolvarilor la quiz.');
    } finally {
      setQuizSubsLoadingByTemaId((p) => ({ ...p, [temaId]: false }));
    }
  };

  const openQuizEditorForTema = async (temaId: number) => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const resp = await api.get(`/api/teme/${temaId}/quiz`);
      const data = resp.data as any;
      const draft: QuizDraft = {
        time_limit_seconds: data?.time_limit_seconds ?? null,
        questions: Array.isArray(data?.questions)
          ? data.questions.map((q: any) => ({
              text: String(q?.text || ''),
              kind: (q?.kind === 'multi' ? 'multi' : 'single') as 'single' | 'multi',
              options: Array.isArray(q?.options)
                ? q.options.map((o: any) => ({ text: String(o?.text || ''), is_correct: Boolean(o?.is_correct) }))
                : [],
            }))
          : [],
      };
      setQuizEditDraft(draft);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setQuizEditDraft(null);
      } else {
        setError(err?.response?.data?.detail || 'Eroare la incarcarea quizului.');
        return;
      }
    } finally {
      setLoading(false);
    }
    setQuizEditingTemaId(temaId);
    setQuizOpen(true);
  };

  const handleUpdateTitle = async (id: number) => {
    if (!selectedConfigId) return;
    const nextTitle = newTitle.trim();
    if (!nextTitle) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const form = new FormData();
      form.append('title', nextTitle);
      await api.patch(`/api/teme/actualizeaza/${id}`, form);
      setEditingTitleId(null);
      setNewTitle('');
      setInfo('Titlul a fost actualizat.');
      await loadTeme(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la actualizarea titlului.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateComment = async (id: number) => {
    if (!selectedConfigId) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const form = new FormData();
      form.append('comment', newComment);
      await api.patch(`/api/teme/actualizeaza/${id}`, form);
      setEditingCommentId(null);
      setNewComment('');
      setInfo('Comentariul a fost actualizat.');
      await loadTeme(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la actualizarea comentariului.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadExtraFiles = async (homeworkId: number, extraFiles: FileList | null) => {
    if (!selectedConfigId) return;
    if (!extraFiles || extraFiles.length === 0) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const formData = new FormData();
      Array.from(extraFiles).forEach((f) => formData.append('files', f));
      await api.post(`/api/teme/adauga-fisiere/${homeworkId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setInfo('Fisierele au fost adaugate.');
      await loadTeme(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la incarcarea fisierelor.');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    if (!selectedConfigId) {
      await loadConfigs();
      return;
    }
    await Promise.all([loadSubjects(selectedConfigId, periodIndex), loadTeme(selectedConfigId, periodIndex)]);
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Teme</h2>
            <p className="text-sm text-muted-foreground">Incarci teme pe materiile tale din catalogul selectat.</p>
          </div>
          <Button variant="outline" iconName="RefreshCcw" iconPosition="left" onClick={refresh} disabled={loading}>
            Reincarca
          </Button>
        </div>

        {error && <div className="mt-3 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>}
        {info && <div className="mt-3 bg-success/10 border border-success/20 text-success rounded-md p-3 text-sm">{info}</div>}

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
          <label className="block text-sm font-medium text-foreground">
            Catalog
            <select
              value={selectedConfigId ?? ''}
              onChange={(e) => setSelectedConfigId(e.target.value ? Number(e.target.value) : null)}
              className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={loading}
            >
              <option value="">-- selecteaza --</option>
              {configs.map((c) => (
                <option key={c.id} value={c.id}>
                  {cfgLabel(c)}
                </option>
              ))}
            </select>
          </label>

          <div className="lg:col-span-2">
            {selectedConfig ? (
              <div className="mt-6 lg:mt-0 flex flex-wrap gap-2">
                {periodLabels(selectedConfig.structure).map((label, idx) => (
                  <button
                    key={label}
                    onClick={() => setPeriodIndex(idx)}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors duration-150 ${
                      periodIndex === idx ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'
                    }`}
                    disabled={loading}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground mt-6 lg:mt-0">Alege un catalog ca sa vezi perioadele.</div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Adauga tema</h3>

          {!selectedConfigId ? (
            <div className="text-sm text-muted-foreground">Selecteaza un catalog.</div>
          ) : subjectsLoading ? (
            <div className="text-sm text-muted-foreground">Se incarca materiile…</div>
          ) : mySubjects.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Nu ai materii in aceasta perioada. Adauga mai intai o materie in Catalog (Materiile mele).
            </div>
          ) : (
            <form className="grid grid-cols-1 lg:grid-cols-2 gap-3" onSubmit={handleUpload}>
              <label className="block text-sm font-medium text-foreground">
                Materie *
                <select
                  value={selectedSubjectId ?? ''}
                  onChange={(e) => setSelectedSubjectId(e.target.value ? Number(e.target.value) : null)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={loading}
                >
                  {mySubjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {(s.name || '').toString().trim() || `Materia #${s.id}`}
                      {s.professor_name ? ` (${s.professor_name})` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <Input label="Titlu *" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Tema 1" />
              <Input
                label="Comentariu (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Instructiuni"
              />
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-1">Fisiere (optional)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
                  className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-muted file:text-foreground hover:file:bg-muted/80"
                />
              </div>
              <div className="lg:col-span-2 flex items-center justify-end">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => {
                      setQuizEditingTemaId(null);
                      setQuizEditDraft(null);
                      setQuizOpen(true);
                    }}
                    disabled={loading}
                    iconName="HelpCircle"
                  >
                    Quiz
                  </Button>
                  <Button variant="default" iconName="Upload" iconPosition="left" type="submit" disabled={loading}>
                    Incarca
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      <QuizBuilderModal
        open={quizOpen}
        initial={quizEditingTemaId != null ? quizEditDraft : quizDraft}
        onClose={() => {
          setQuizOpen(false);
          setQuizEditingTemaId(null);
          setQuizEditDraft(null);
        }}
        onSave={(d) => {
          if (quizEditingTemaId == null) {
            setQuizDraft(d);
            setInfo('Quiz pregatit. Apasa „Incarca” ca sa salvezi tema + quiz.');
            setQuizOpen(false);
            return;
          }
          void (async () => {
            setLoading(true);
            setError(null);
            setInfo(null);
            try {
              await api.put(`/api/teme/${quizEditingTemaId}/quiz`, d);
              setInfo('Quiz salvat.');
              if (selectedConfigId) await loadTeme(selectedConfigId, periodIndex);
              setQuizOpen(false);
              setQuizEditingTemaId(null);
              setQuizEditDraft(null);
            } catch (err: any) {
              setError(err?.response?.data?.detail || 'Eroare la salvarea quizului.');
            } finally {
              setLoading(false);
            }
          })();
        }}
        showReactivateAction={quizEditingTemaId != null}
        onSaveAndReactivate={
          quizEditingTemaId != null
            ? (d) => {
                void (async () => {
                  setLoading(true);
                  setError(null);
                  setInfo(null);
                  try {
                    await api.put(`/api/teme/${quizEditingTemaId}/quiz`, d, {
                      params: { reactivate: 1 },
                    });
                    setInfo('Quiz salvat si reactivat. Rezolvarile anterioare au fost resetate.');
                    if (selectedConfigId) await loadTeme(selectedConfigId, periodIndex);
                    setQuizOpen(false);
                    setQuizEditingTemaId(null);
                    setQuizEditDraft(null);
                  } catch (err: any) {
                    setError(err?.response?.data?.detail || 'Eroare la salvarea/reactivarea quizului.');
                  } finally {
                    setLoading(false);
                  }
                })();
              }
            : undefined
        }
        onClear={() => {
          if (quizEditingTemaId == null) {
            setQuizDraft(null);
            return;
          }
          void (async () => {
            setLoading(true);
            setError(null);
            setInfo(null);
            try {
              await api.delete(`/api/teme/${quizEditingTemaId}/quiz`);
              setInfo('Quiz sters.');
              if (selectedConfigId) await loadTeme(selectedConfigId, periodIndex);
              setQuizOpen(false);
              setQuizEditingTemaId(null);
              setQuizEditDraft(null);
            } catch (err: any) {
              setError(err?.response?.data?.detail || 'Eroare la stergerea quizului.');
            } finally {
              setLoading(false);
            }
          })();
        }}
      />

      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">Teme incarcate</h3>
          <div className="text-xs text-muted-foreground">{teme.length} teme</div>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground mt-3">Se incarca…</div>
        ) : teme.length === 0 ? (
          <div className="text-sm text-muted-foreground mt-3">Nu exista teme create in aceasta perioada.</div>
        ) : (
          <div className="mt-3 space-y-6">
            {grouped.map((group) => (
              <div key={`${group.subjectId ?? 'null'}:${group.subjectName}`} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-foreground">{group.subjectName}</div>
                  <div className="text-xs text-muted-foreground">{group.items.length}</div>
                </div>

                {group.items.map((tema) => (
                  <div key={tema.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        {editingTitleId === tema.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              value={newTitle}
                              onChange={(e) => setNewTitle(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                            <Button size="sm" variant="default" onClick={() => handleUpdateTitle(tema.id)}>
                              Salveaza
                            </Button>
                          </div>
                        ) : (
                           <div className="flex items-center gap-2">
                             <h4 className="text-sm font-semibold text-foreground truncate">{tema.title}</h4>
                             {tema.has_quiz ? (
                               <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary whitespace-nowrap">
                                 Quiz
                               </span>
                             ) : null}
                             {tema.has_quiz ? (
                               <Button
                                 variant="outline"
                                 size="xs"
                                 iconName="HelpCircle"
                                 onClick={() => void openQuizEditorForTema(tema.id)}
                               >
                                 Vezi quiz
                               </Button>
                             ) : null}
                              <button
                                className="p-1 rounded hover:bg-muted"
                                onClick={() => {
                                  setEditingTitleId(tema.id);
                                 setNewTitle(tema.title);
                               }}
                               aria-label="Editeaza titlu"
                             >
                              <Icon name="Pencil" size={16} className="text-muted-foreground" />
                            </button>
                          </div>
                        )}

                        {editingCommentId === tema.id ? (
                          <div className="mt-2 space-y-2">
                            <textarea
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                              rows={3}
                            />
                            <div className="flex justify-end">
                              <Button size="sm" variant="default" onClick={() => handleUpdateComment(tema.id)}>
                                Salveaza
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-1 flex items-start gap-2">
                            <p className="text-sm text-muted-foreground whitespace-pre-line flex-1">{tema.description || '—'}</p>
                            <button
                              className="p-1 rounded hover:bg-muted"
                              onClick={() => {
                                setEditingCommentId(tema.id);
                                setNewComment(tema.description || '');
                              }}
                              aria-label="Editeaza comentariu"
                            >
                              <Icon name="FilePenLine" size={16} className="text-muted-foreground" />
                            </button>
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                          <Icon name="Calendar" size={14} className="text-muted-foreground" />
                          <span>{new Date(tema.created_at).toLocaleString()}</span>
                        </p>
                      </div>

                      <div className="flex items-center gap-1">
                        <label className="p-2 rounded-md hover:bg-muted transition-colors duration-150 cursor-pointer" aria-label="Adauga fisiere">
                          <Icon name="Paperclip" size={18} className="text-muted-foreground" />
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              handleUploadExtraFiles(tema.id, e.target.files);
                              e.currentTarget.value = '';
                            }}
                          />
                        </label>
                        <button
                          className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                          onClick={() => void openQuizEditorForTema(tema.id)}
                          aria-label={tema.has_quiz ? 'Editeaza quiz' : 'Adauga quiz'}
                          disabled={loading}
                        >
                          <Icon
                            name="HelpCircle"
                            size={18}
                            className={tema.has_quiz ? 'text-primary' : 'text-muted-foreground'}
                          />
                        </button>
                        <button
                          className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                          onClick={() => handleDeleteTema(tema.id)}
                          aria-label="Sterge tema"
                        >
                          <Icon name="Trash2" size={18} className="text-error" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-4">
                      <p className="text-sm font-medium text-foreground mb-2">Fisiere</p>
                      {tema.files?.length ? (
                        <ul className="space-y-1">
                          {tema.files.map((f, idx) => (
                            <li key={`${tema.id}-${idx}`} className="text-sm">
                              <button className="text-primary hover:underline" onClick={() => void doDownloadHomeworkFile(Number(f.id))}>
                                {f.original_name}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-muted-foreground">—</p>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-medium text-foreground">Teme rezolvate de elevi</div>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName={solutionsOpen[tema.id] ? 'ChevronUp' : 'ChevronDown'}
                          iconPosition="right"
                          onClick={() => {
                            setSolutionsOpen((p) => ({ ...p, [tema.id]: !p[tema.id] }));
                            if (!solutionsByTemaId[tema.id]) void loadSolutions(tema.id);
                          }}
                        >
                          {solutionsOpen[tema.id] ? 'Ascunde' : 'Vezi'}
                        </Button>
                      </div>

                      {solutionsOpen[tema.id] && (
                        <div className="mt-3">
                          {solutionsLoadingByTemaId[tema.id] ? (
                            <div className="text-sm text-muted-foreground">Se incarca…</div>
                          ) : (solutionsByTemaId[tema.id] || []).length === 0 ? (
                            <div className="text-sm text-muted-foreground">Nu exista teme incarcate de elevi pentru aceasta tema.</div>
                          ) : (
                            (() => {
                              const list = solutionsByTemaId[tema.id] || [];
                              const selectedStudentId = selectedStudentByTemaId[tema.id] || '';
                              const selected =
                                list.find((s) => String(s.student?.id) === String(selectedStudentId)) || list[0] || null;
                              return (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <label className="text-sm text-muted-foreground">Elev</label>
                                    <select
                                      value={selectedStudentId}
                                      onChange={(e) => setSelectedStudentByTemaId((p) => ({ ...p, [tema.id]: e.target.value }))}
                                      className="min-w-[220px] sm:min-w-[280px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                    >
                                      {list.map((s) => (
                                        <option key={s.id} value={String(s.student?.id ?? '')}>
                                          {(s.student?.full_name || '').toString() ||
                                            (s.student?.email || '').toString() ||
                                            `Elev #${s.student?.id}`}
                                        </option>
                                      ))}
                                    </select>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      iconName="RefreshCcw"
                                      iconPosition="left"
                                      onClick={() => void loadSolutions(tema.id)}
                                    >
                                      Reincarca
                                    </Button>
                                  </div>

                                  {selected && (
                                    <div className="border border-border rounded-lg p-3">
                                      <div className="text-xs text-muted-foreground">
                                        Încărcat: {new Date(selected.updated_at || selected.created_at).toLocaleString('ro-RO')}
                                      </div>
                                      {selected.comment && (
                                        <div className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{selected.comment}</div>
                                      )}

                                      <div className="mt-3">
                                        <div className="text-sm font-medium text-foreground mb-2">Fișiere</div>
                                        {selected.files?.length ? (
                                          <ul className="space-y-1">
                                            {selected.files.map((f, idx) => (
                                              <li key={`${selected.id}-${idx}`} className="text-sm">
                                                <button className="text-primary hover:underline" onClick={() => void doDownloadSubmissionFile(Number(f.id))}>
                                                  {f.original_name}
                                                </button>
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <div className="text-sm text-muted-foreground">—</div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                          )}
                        </div>
                      )}
                    </div>

                    {tema.has_quiz ? (
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-medium text-foreground">Quiz rezolvate</div>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName={quizSubsOpen[tema.id] ? 'ChevronUp' : 'ChevronDown'}
                            iconPosition="right"
                            onClick={() => {
                              setQuizSubsOpen((p) => ({ ...p, [tema.id]: !p[tema.id] }));
                              if (!quizSubsByTemaId[tema.id]) void loadQuizSubmissions(tema.id);
                            }}
                          >
                            {quizSubsOpen[tema.id] ? 'Ascunde' : 'Vezi'}
                          </Button>
                        </div>

                        {quizSubsOpen[tema.id] && (
                          <div className="mt-3">
                            {quizSubsLoadingByTemaId[tema.id] ? (
                              <div className="text-sm text-muted-foreground">Se incarca…</div>
                            ) : (quizSubsByTemaId[tema.id] || []).length === 0 ? (
                              <div className="text-sm text-muted-foreground">Nu exista quizuri trimise de elevi pentru aceasta tema.</div>
                            ) : (
                              (() => {
                                const list = quizSubsByTemaId[tema.id] || [];
                                const selectedStudentId = selectedStudentByQuizTemaId[tema.id] || '';
                                const selected =
                                  list.find((s) => String(s.student?.id) === String(selectedStudentId)) || list[0] || null;
                                return (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <label className="text-sm text-muted-foreground">Elev</label>
                                      <select
                                        value={selectedStudentId}
                                        onChange={(e) => setSelectedStudentByQuizTemaId((p) => ({ ...p, [tema.id]: e.target.value }))}
                                        className="min-w-[220px] sm:min-w-[280px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                      >
                                        {list.map((s) => (
                                          <option key={s.id} value={String(s.student?.id ?? '')}>
                                            {(s.student?.full_name || '').toString() ||
                                              (s.student?.email || '').toString() ||
                                              `Elev #${s.student?.id}`}
                                          </option>
                                        ))}
                                      </select>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        iconName="RefreshCcw"
                                        iconPosition="left"
                                        onClick={() => void loadQuizSubmissions(tema.id)}
                                      >
                                        Reincarca
                                      </Button>
                                    </div>

                                    {selected && (
                                      <div className="border border-border rounded-lg p-3">
                                        <div className="text-xs text-muted-foreground">
                                          Trimite: {selected.submitted_at ? new Date(selected.submitted_at).toLocaleString('ro-RO') : '—'}
                                        </div>
                                        <div className="mt-2 text-sm text-muted-foreground">
                                          Scor: {selected.score ?? '—'}/{selected.total ?? '—'}
                                          {selected.duration_seconds != null ? ` • Timp: ${Math.max(0, Math.round(selected.duration_seconds / 60))} min` : ''}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()
                            )}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <SubscriptionRequiredModal open={upgradeOpen} feature="materials_download" onClose={() => setUpgradeOpen(false)} />
    </div>
  );
};

export default TemePanel;
